import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import {
  useHistory, useLocation, useParams, useRouteMatch, Switch, Route, Redirect,
} from 'react-router-dom';

import { usePostHog } from 'posthog-js/react';

import { AccountIntent, ActivationProvider, useActivation } from './ActivationContext';
import { AuthMethodSelection } from './steps/AuthMethodSelection';
import { OtpVerification } from './steps/OtpVerification';
import { CoverageForm } from './steps/CoverageForm';
import { ActivationComplete } from './steps/ActivationComplete';
import { EmailCallback } from './steps/EmailCallback';
import { EmailEntry } from './steps/EmailEntry';
import { EmailSent } from './steps/EmailSent';

import { getSupabaseClient } from '@/utils/supabaseClient';
import { buildRedirectParam, isSafeRedirectUri } from '@/utils/urlHelpers';
import { activateSleepioConfirm } from '@/utils/sleepioActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { usePostHogDistinctId } from '@/hooks/usePostHogDistinctId';
import { SLEEPIO_CANOPY_PROGRAM } from '@/utils/posthog';

interface RouteParams {
  lob: string;
  // eslint-disable-next-line camelcase
  org_slug: string;
}

const ActivationRouterInner: React.FC = () => {
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const { state, dispatch } = useActivation();
  const [sessionChecked, setSessionChecked] = useState(false);
  const posthog = usePostHog();
  const { distinctId: partnerDistinctId } = usePostHogDistinctId();

  // Stitch a partner-originated anonymous PostHog session into this CAP session.
  // When a landing page passes ph_token or ph_distinct_id in the URL, adopt that
  // identity so all activation events land on the same person as the pre-CAP funnel.
  useEffect(() => {
    if (partnerDistinctId) {
      posthog?.identify(partnerDistinctId);
    }
  }, [partnerDistinctId, posthog]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectTo = params.get('redirectTo');
    if (redirectTo && isSafeRedirectUri(redirectTo)) {
      dispatch({ type: 'SET_REDIRECT_TO', redirectTo });
      sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectTo);
    }
  }, [dispatch, location.search]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await getSupabaseClient().auth.getSession();
        if (session) {
          dispatch({ type: 'SET_SESSION', session });
          const redirectParam = buildRedirectParam(
            new URLSearchParams(location.search).get('redirectTo'),
          );
          if (state.accountIntent === AccountIntent.Signin) {
            // A fresh magic-link login also lands here with a session already set —
            // don't count that as a "resumed" session.
            if (!location.pathname.includes('/email/callback')) {
              posthog?.capture('LoginSessionResumed', { program: SLEEPIO_CANOPY_PROGRAM });
            }
            dispatch({ type: 'SET_STEP', step: 'activation_complete' });
            history.replace(`${url}/complete${redirectParam}`);
          } else {
            try {
              await activateSleepioConfirm({ accessToken: session.access_token });
              dispatch({ type: 'SET_STEP', step: 'coverage_form' });
              history.replace(`${url}/coverage${redirectParam}`);
            } catch {
              await getSupabaseClient().auth.signOut();
              history.replace(`${url}/auth${redirectParam}`);
            }
          }
        }
      } finally {
        setSessionChecked(true);
      }
    };
    checkSession();
  }, []); // eslint-disable-line

  if (!sessionChecked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const authFallbackUrl = `${url}/auth${buildRedirectParam(
    new URLSearchParams(location.search).get('redirectTo'),
  )}`;

  return (
    <Switch>
      <Route exact path={`${path}/auth`} component={AuthMethodSelection} />
      <Route exact path={`${path}/otp`} component={OtpVerification} />
      <Route exact path={`${path}/email`} component={EmailEntry} />
      <Route exact path={`${path}/email/callback`} component={EmailCallback} />
      <Route exact path={`${path}/sent`} component={EmailSent} />
      <Route exact path={`${path}/coverage`} component={CoverageForm} />
      <Route exact path={`${path}/complete`} component={ActivationComplete} />
      <Redirect to={authFallbackUrl} />
    </Switch>
  );
};

const ActivationRouter: React.FC = () => {
  // eslint-disable-next-line camelcase
  const { org_slug: orgSlug } = useParams<RouteParams>();
  const { pathname } = useLocation();
  const accountIntent = pathname.includes('/sign-up') ? AccountIntent.Create : AccountIntent.Signin;

  return (
    <ActivationProvider initialOrgSlug={orgSlug} initialAccountIntent={accountIntent}>
      <ActivationRouterInner />
    </ActivationProvider>
  );
};

export default ActivationRouter;
